// Shared browser contract for company-owned initial admission. Local storage,
// checkout returns and timers are never proof that onboarding is complete.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ACTIONS = new Set(['subscription', 'payment_pending', 'welcome', 'workspace']);
const CYCLES = new Set(['monthly', 'annual']);
const boundCompanyId = value => typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value);

// Account-level discovery carries one company identifier, never a destination,
// actor or payment assertion. The selected company must still be reauthorized.
export function normalizeCompanyOnboardingContinuation(response) {
  const value = response?.continuation;
  if (!response || typeof response !== 'object' || Array.isArray(response)
    || Object.keys(response).length !== 1 || !Object.hasOwn(response, 'continuation')
    || !value || typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).length !== 1 || !Object.hasOwn(value, 'companyId')
    || (value.companyId !== null && (typeof value.companyId !== 'string' || !UUID.test(value.companyId)))) {
    throw new Error('company_onboarding_continuation_invalid');
  }
  return Object.freeze({ companyId: value.companyId });
}

export function normalizeCompanyOnboarding(response, companyId) {
  const value = response?.onboarding;
  const time = input => typeof input === 'string' && input.length <= 40 && Number.isFinite(Date.parse(input)) ? input : null;
  if (!boundCompanyId(companyId) || !value || value.companyId !== companyId
    || !['enrolled', 'required', 'completed'].every(key => typeof value[key] === 'boolean')
    || !ACTIONS.has(value.nextAction)
    || (value.selectedCycle !== null && !CYCLES.has(value.selectedCycle))
    || (value.required && (!value.enrolled || value.completed))
    || (value.completed && !value.enrolled)
    || (value.enrolled && !value.required && !value.completed)
    || (value.required && !['subscription', 'payment_pending'].includes(value.nextAction))
    || (!value.required && ['subscription', 'payment_pending'].includes(value.nextAction))
    || (value.nextAction === 'welcome' && (!value.enrolled || !value.completed))
    || value.enrolled !== Boolean(time(value.enrolledAt))
    || value.completed !== Boolean(time(value.admittedAt))
    || value.completed !== ['payment', 'exemption'].includes(value.admissionBasis)
    || (!value.enrolled && value.selectedCycle !== null)) {
    throw new Error('company_onboarding_response_invalid');
  }
  return Object.freeze({
    companyId, enrolled: value.enrolled, required: value.required, completed: value.completed,
    selectedCycle: value.selectedCycle,
    admissionBasis: ['payment', 'exemption'].includes(value.admissionBasis) ? value.admissionBasis : null,
    admittedAt: time(value.admittedAt), enrolledAt: time(value.enrolledAt), nextAction: value.nextAction,
  });
}

export function companyOnboardingOptions(companyId) {
  if (!boundCompanyId(companyId)) throw new Error('company_onboarding_company_required');
  return { headers: { 'x-company-id': companyId }, redirectOnUnauthorized: false };
}

export function companyOnboardingWelcomeHref(companyId) {
  if (!UUID.test(companyId || '')) throw new Error('company_onboarding_company_required');
  return `/signup?onboarding=1&company=${encodeURIComponent(companyId)}`;
}

export function companyOnboardingEntry(search = '') {
  const params = new URLSearchParams(search);
  return params.getAll('onboarding').length === 1 && params.get('onboarding') === '1'
    && params.getAll('company').length === 1 && UUID.test(params.get('company') || '')
    ? params.get('company') : null;
}

export function isCompanyCheckoutReturn(location = {}) {
  const search = new URLSearchParams(location.search || '');
  const query = new URLSearchParams(String(location.hash || '').split('?')[1] || '');
  return search.get('billing_checkout') === 'returned' || query.get('checkout') === 'returned';
}
